/* global _IFRAMELY_KEY */

import React from 'react';
import PropTypes from 'prop-types';
import urlParse from 'url-parse';
import $ from 'jquery';
import moment from 'moment';
import _uniqueId from 'lodash/uniqueId';
import _get from 'lodash/get';

import iframely from '../../vendor/iframely/iframely-loader';
import { Style } from './Style';
import { mountTargetBasic, unmountTarget } from '../mountableBasic';
import './IFramelyMedia.scss';

// https://iframely.com/docs/links
const REL_TYPE_PLAYER = 'player';
const REL_TYPE_IMAGE = 'image';
const REL_TYPE_GIFV = 'gifv';
const REL_TYPE_APP = 'app';
const REL_TYPE_THUMBNAIL = 'thumbnail';
const REL_TYPE_READER = 'reader';
const REL_TYPE_SURVEY = 'survey';
const REL_TYPE_SUMMARY = 'summary';
const REL_TYPE_FILE = 'file';
const REL_TYPE_ICON = 'icon';
const REL_TYPE_LOGO = 'logo';

const REL_TYPES = [
  REL_TYPE_PLAYER,
  REL_TYPE_IMAGE,
  REL_TYPE_GIFV,
  REL_TYPE_APP,
  REL_TYPE_THUMBNAIL,
  REL_TYPE_READER,
  REL_TYPE_SURVEY,
  REL_TYPE_SUMMARY,
  REL_TYPE_FILE,
  REL_TYPE_ICON,
  REL_TYPE_LOGO,
];

const getRelType = (rels, choices) => {
  return rels.find((x) => choices.indexOf(x) > -1);
};

export class IFramelyMedia extends React.Component {
  static propTypes = {
    url: PropTypes.string.isRequired,
    maxWidth: PropTypes.number,
    maxHeight: PropTypes.number,
    autoSize: PropTypes.bool,
    autoPlay: PropTypes.bool,
    inlineCss: PropTypes.string, // not sure if this is used

    // This is a workaround until I can figure out the right way of doing it
    // eslint-disable-next-line react/forbid-prop-types
    targetDocument: PropTypes.any,
  };

  static defaultProps = {
    maxWidth: null,
    maxHeight: null,
    autoSize: false,
    autoPlay: false,
    inlineCss: null,

    targetDocument: document,
  };

  state = {
    isLoading: true,
    mediaType: null,
    mediaData: null,
    mediaId: _uniqueId('iframely-media-'),
  };

  componentDidMount() {
    const { url, autoPlay, maxHeight, maxWidth } = this.props;
    // https://iframely.com/docs/oembed-api
    const urlInfo = urlParse('https://iframe.ly/api/iframely');
    urlInfo.query = {
      iframe: 1,
      lazy: 1,
      omit_css: 1,
      align: 'left',
      url,
      // MD5 hash of the iframely API KEY
      key: _IFRAMELY_KEY,
    };

    // check if iframely is loaded
    if (iframely) {
      urlInfo.query.omit_script = 1;
    }
    if (autoPlay) {
      urlInfo.query.autoplay = 1;
    }
    if (maxHeight) {
      urlInfo.query.maxheight = maxHeight;
    }
    if (maxWidth) {
      urlInfo.query.maxwidth = maxWidth;
    }

    // TODO: support error in loading
    $.ajax({
      dataType: 'json',
      url: urlInfo.toString(),
    }).done((response) => {
      const mediaType = getRelType(response.rel, REL_TYPES);

      this.setState({
        isLoading: false,
        mediaType,
        mediaData: response,
      });
    });
  }

  renderDefault = () => {
    const { url } = this.props;

    return (
      <a href={url} target="_top" className="iframely-media-loading">
        {url}
      </a>
    );
  };

  // This renders the summary that is clipped by iframely's responsive sizing
  renderSummary = () => {
    const { mediaType, mediaData } = this.state;
    const showCustomSummary =
      [REL_TYPE_SUMMARY].indexOf(mediaType) !== -1 ||
      mediaData.rel.length === 0;

    if (!showCustomSummary) {
      return null;
    }

    const icons = _get(mediaData, ['links', 'icon'], []);
    let icon = null;
    if (icons.length > 0) {
      const favicon = [].concat(icons).pop();
      icon = (
        <>
          <img
            src={favicon.href}
            alt=""
            aria-hidden="true"
            className="favicon"
          />
          <span className="site-name">{mediaData.meta.site}</span>
        </>
      );
    }

    let date = null;
    if (mediaData.meta.date) {
      date = moment(mediaData.meta.date).format('D MMM YYYY');
    }

    return (
      <div className="custom-summary">
        <a href={mediaData.url} className="title" target="blank">
          {mediaData.meta.title}
        </a>
        {mediaData.meta.description && (
          <p className="description">{mediaData.meta.description}</p>
        )}
        <div className="extra">
          {icon && <span className="info info-icon">{icon}</span>}
          {mediaData.meta.author && (
            <span className="info info-author">{mediaData.meta.author}</span>
          )}
          {date && <span className="info info-date">{date}</span>}
        </div>
      </div>
    );
  };

  renderMedia = () => {
    // eg. https://www.youtube.com/watch?v=nTCNt7cJi9Y (player)
    // eg. http://oldweb.today/ (not much metadata)
    const {
      autoSize,
      inlineCss,
      targetDocument,
      maxWidth,
      maxHeight,
    } = this.props;
    const { mediaData, mediaId, mediaType } = this.state;

    // wrapper for setting max width/height and alignment
    const cssRules = [];
    const css = {};

    if (maxWidth) {
      css.width = `${maxWidth}px`;
    }
    if (maxHeight) {
      css.height = `${maxHeight}px`;
    }
    if (!maxWidth || autoSize) {
      css.width = '100%';
    }

    const wrapperCss = Object.entries(css)
      .map(([key, val]) => `${key}: ${val};`)
      .join(' ');
    cssRules.push(`#${mediaId} { ${wrapperCss} }`);

    if (inlineCss) {
      cssRules.push(`#${mediaId} { ${inlineCss} }`);
    }

    const divProps = {
      id: mediaId,
      ref: this.refMediaDiv,
      className: `iframely-media-${mediaType}`,
    };
    const dangerousHtml = {
      __html: mediaData.html,
    };

    return (
      <>
        <Style targetDocument={targetDocument}>{cssRules.join('\n')}</Style>
        {/* eslint-disable-next-line react/no-danger */}
        <div {...divProps} dangerouslySetInnerHTML={dangerousHtml} />
        {this.renderSummary()}
      </>
    );
  };

  refMediaDiv = (div) => {
    if (div && iframely) {
      const targetIframe = div.querySelector('iframe');
      if (targetIframe) {
        iframely.load(targetIframe);
      }
    }
  };

  render() {
    const { isLoading, mediaData } = this.state;
    let content = null;

    // make it a clickable link in case we something went wrong
    if (isLoading || !mediaData) {
      content = this.renderDefault();
    } else {
      content = this.renderMedia();
    }

    return <iframely-media>{content}</iframely-media>;
  }
}

export const mountBasic = (target, props) =>
  mountTargetBasic(IFramelyMedia, target, props);

export const unmount = unmountTarget;
